import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import apiClient from '../utils/api';
import { getCurrentSubdomain } from '../utils/subdomain';

interface StaffUser {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  userType: string;
  roles?: string[];
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<StaffUser | null>(null);
  const accessToken = ref<string | null>(localStorage.getItem('staffAccessToken'));
  const refreshToken = ref<string | null>(localStorage.getItem('staffRefreshToken'));
  const loading = ref(false);
  const error = ref<string | null>(null);

  const gatePost = ref<string | null>(localStorage.getItem('staffGatePost'));

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
  const isSecurityGuard = computed(() => user.value?.userType === 'SECURITY_GUARD');
  const isResidentStaff = computed(() => user.value?.userType === 'RESIDENT_STAFF');
  const hasSelectedPost = computed(() => !isSecurityGuard.value || !!gatePost.value);

  // Re-hydrate user from storage
  const stored = localStorage.getItem('staffUser');
  if (stored) {
    try {
      user.value = JSON.parse(stored);
    } catch {
      /* ignore */
    }
  }

  async function refreshProfile() {
    if (!accessToken.value) return;
    try {
      const { data } = await apiClient.get('/staff/me');
      if (data?.user) {
        const updated = { ...user.value!, ...data.user };
        user.value = updated;
        localStorage.setItem('staffUser', JSON.stringify(updated));
      }
    } catch {
      // Silently fail — stale data is better than breaking the app
    }
  }

  async function login(phone: string, password: string) {
    loading.value = true;
    error.value = null;
    try {
      const subdomain = getCurrentSubdomain();
      const { data } = await apiClient.post(
        '/auth/tenant/login',
        { login: phone, password },
        {
          headers: subdomain ? { 'X-Estate-Subdomain': subdomain } : {},
        }
      );

      const allowedTypes = ['SECURITY_GUARD', 'MAINTENANCE_STAFF', 'RESIDENT_STAFF'];
      if (!allowedTypes.includes(data.user?.userType)) {
        throw new Error('This app is for estate staff only.');
      }

      accessToken.value = data.accessToken;
      refreshToken.value = data.refreshToken;
      user.value = data.user;

      localStorage.setItem('staffAccessToken', data.accessToken);
      localStorage.setItem('staffRefreshToken', data.refreshToken);
      localStorage.setItem('staffUser', JSON.stringify(data.user));
    } catch (err: any) {
      error.value = err.response?.data?.message ?? err.message ?? 'Login failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function selectPost(post: string) {
    gatePost.value = post;
    localStorage.setItem('staffGatePost', post);
  }

  function logout() {
    accessToken.value = null;
    refreshToken.value = null;
    user.value = null;
    gatePost.value = null;
    localStorage.removeItem('staffAccessToken');
    localStorage.removeItem('staffRefreshToken');
    localStorage.removeItem('staffUser');
    localStorage.removeItem('staffGatePost');
    // Estate branding and subdomain are intentionally kept so the login page
    // still shows the correct estate after logout — clear only via "Change estate"
  }

  return {
    user,
    accessToken,
    refreshToken,
    gatePost,
    loading,
    error,
    isAuthenticated,
    isSecurityGuard,
    isResidentStaff,
    hasSelectedPost,
    login,
    refreshProfile,
    selectPost,
    logout,
  };
});
